import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@primitives/form";
import { Button } from "@primitives/button";
import { Input } from "@primitives/input";

import { useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useTranslation } from "react-i18next";

import { getForgotUsernameSchema, ForgotUsernameType } from "@/schemas/auth";
import { forgotUsername } from "@/server/services/authService";
import { useToast } from "@/hooks/useToast";
import { ApiError } from "@/server/apiError";
import { SUPPORT_EMAIL } from "@/config/constants";
import { useCountdown } from "@/hooks/useCountdown";
import { useTurnstile } from "./hooks/useTurnstile";

// Per-email cooldown (matches the backend's 60s limit). Used as the fallback
// when a 429 CooldownActive response doesn't carry a parseable seconds value.
const COOLDOWN_SECONDS = 60;

// The CooldownActive `detail` is a human string ("Please wait N seconds…"); pull
// the first integer out of it, falling back to the known 60s per-email cooldown.
function parseCooldownSeconds(detail: string | undefined): number {
  const match = detail?.match(/\d+/);
  const seconds = match ? Number(match[0]) : NaN;
  return Number.isFinite(seconds) && seconds > 0 ? seconds : COOLDOWN_SECONDS;
}

export default function ForgotUsernameForm() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const { count: cooldown, start: startCooldown } = useCountdown();
  const {
    ref,
    token,
    isReady,
    showError,
    enabled: turnstileEnabled,
    status,
    siteKey,
    onSuccess,
    onError,
    onExpire,
    onBeforeInteractive,
    onAfterInteractive,
    reset,
  } = useTurnstile();

  const form = useForm<ForgotUsernameType>({
    resolver: zodResolver(getForgotUsernameSchema(t)),
    defaultValues: { email: "" },
  });

  const showErrorToast = (description: string) =>
    toast({ duration: 3000, description, variant: "destructive" });

  async function onSubmit({ email }: ForgotUsernameType) {
    try {
      // Lowercase/trim before sending (§3.4) — never rely on exact casing.
      await forgotUsername({
        email: email.trim().toLowerCase(),
        turnstileToken: token,
      });
      // 200 means "request accepted", NOT "email delivered" (fire-and-forget,
      // and some accounts can't self-recover yet). Show ONE generic message
      // regardless of whether an account exists — never infer existence.
      setSubmitted(true);
    } catch (error) {
      if (error instanceof ApiError) {
        // Branch on the numeric status first (per the API contract).
        switch (error.status) {
          case 400:
            // The BE kill-switch (FORGOT_USERNAME_ENABLED=false) also answers
            // 400 — check the title before treating it as a validation
            // failure, or a disabled flow reads as "invalid email".
            if (error.data?.title === "errors.ForgotUsername.NotEnabled") {
              setUnavailable(true);
              break;
            }
            form.setError("email", {
              message: t("forgotUsername.invalidEmail"),
            });
            break;
          case 403:
            // Turnstile missing/failed — widget is reset in `finally`, retry.
            showErrorToast(t(error.data?.title ?? "errors.Turnstile.Failed"));
            break;
          case 429: {
            // Rate limited. CooldownActive carries seconds remaining and gets a
            // live countdown; the daily/hourly caps just surface a message.
            if (error.data?.title === "errors.ForgotUsername.CooldownActive") {
              startCooldown(parseCooldownSeconds(error.data?.detail));
            }
            showErrorToast(
              t(error.data?.title ?? "errors.ForgotUsername.CooldownActive"),
            );
            break;
          }
          case 503:
            // Rate-limiter dependency briefly down (fail-closed) — transient.
            showErrorToast(t("errors.ForgotUsername.ServiceUnavailable"));
            break;
          default:
            showErrorToast(t("errorOccurred"));
        }
      } else {
        showErrorToast(
          error instanceof Error ? error.message : t("errorOccurred"),
        );
      }
    } finally {
      // Tokens are single-use — reset the widget so a retry gets a fresh one.
      reset();
    }
  }

  // Feature disabled on the backend — same layout as the confirmation state;
  // the page wrapper owns the "Back to login" link.
  if (unavailable) {
    return (
      <div className="my-7 space-y-4 text-center">
        <p className="text-sm text-muted-foreground">
          {t("errors.ForgotUsername.NotEnabled")}
        </p>
        <p className="text-sm text-muted-foreground">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-secondary underline"
          >
            {t("forgotUsername.contactUs")}
          </a>
        </p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="my-7 space-y-4 text-center">
        <p className="text-sm text-muted-foreground">
          {t("forgotUsername.confirmation")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("forgotUsername.didntReceive")}{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-secondary underline"
          >
            {t("forgotUsername.contactUs")}
          </a>
        </p>
      </div>
    );
  }

  const isCoolingDown = cooldown > 0;

  return (
    <div className="my-7 space-y-4">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">{t("forgotUsername.title")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("forgotUsername.description")}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="email"
                    className="rounded-lg border-border bg-card py-6 placeholder:opacity-70"
                    placeholder={t("forgotUsername.emailPlaceholder")}
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    {...field}
                  />
                </FormControl>
                {/* Message is localized via the schema factory (client-side
                    validation) and the server 400 handler — never raw English. */}
                <FormMessage />
              </FormItem>
            )}
          />

          {turnstileEnabled && (
            <Turnstile
              className="[&>div>iframe]:!w-full [&>div]:!w-full"
              style={{ width: "100%" }}
              ref={ref}
              siteKey={siteKey}
              options={{ appearance: "interaction-only", size: "flexible" }}
              onSuccess={onSuccess}
              onError={onError}
              onExpire={onExpire}
              onBeforeInteractive={onBeforeInteractive}
              onAfterInteractive={onAfterInteractive}
            />
          )}

          {showError && (
            <p role="alert" className="text-sm text-destructive">
              {t("errors.Turnstile.Blocked")}
            </p>
          )}

          <Button
            type="submit"
            disabled={form.formState.isSubmitting || !isReady || isCoolingDown}
            className="mt-10 h-12 w-full rounded-lg"
          >
            {form.formState.isSubmitting
              ? t("forgotUsername.submitting")
              : isCoolingDown
                ? t("forgotUsername.retryIn", { seconds: cooldown })
                : status === "loading"
                  ? t("forgotUsername.verifying")
                  : t("forgotUsername.submit")}
          </Button>
        </form>
      </Form>
    </div>
  );
}
